import { useFormik } from 'formik';
import { useMutation, useQuery } from 'react-query';
import { PEOPLE_SEX } from '../../../constants';
import { FormGroup } from '../../../ui/form/FormGroup';
import { Select } from '../../../ui/form/Select';
import { Input } from '../../../ui/form/Input';
import { useRoles, useTeams, useOffices, usePeople } from '../hooks';
import { createUser } from '../../../network';
import { useState, useMemo, useEffect } from 'react';
import { Dialog } from 'evergreen-ui';
import { AddUserIcon } from '../../../assets/AddUserIcon';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/20/solid';
import { useToast } from '../../../utils/useToast';
import * as Yup from 'yup';
import officesManagement from '../../../network/officesManagement';
import axios from 'axios';
import {
  CustomFieldDefinition,
  listCustomFields,
} from '../../../network/customFields';

// Country code mapping for Postcoder (used by the backend)
const countryCodeMapping: { [key: string]: string } = {
  GB: 'uk',
  DE: 'de',
  NL: 'nl',
  PL: 'pl',
  FR: 'fr',
  IE: 'ie',
  US: 'us',
  CA: 'ca',
  AU: 'au',
  NZ: 'nz',
  // Add more mappings as needed
};

type Props = {
  isOpen: boolean;
  onClose(): void;
  refetch(): void;
};

export function AddUserDialog({ isOpen, onClose, refetch }: Props) {
  const { mutateAsync } = useMutation(createUser);
  const { data: role_data } = useRoles();
  const { data: team_data } = useTeams();
  const { data: office_data } = useOffices();
  const { data: people_data } = usePeople();
  const [secondStep, setSecondStep] = useState<boolean>(false);
  const { toast } = useToast();

  const { data: customFieldResponse } = useQuery({
    queryKey: ['custom-fields', 'person'],
    queryFn: () => listCustomFields({ scope: 'person', active: true }),
  });
  const customFields: CustomFieldDefinition[] = customFieldResponse?.data || [];
  
  // Address related states
  const [countries, setCountries] = useState<any[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [addressQuery, setAddressQuery] = useState<string>('');
  const [addressResults, setAddressResults] = useState<any[]>([]);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);

  // Fetch countries when component mounts
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const data = await officesManagement.getCountries();
        setCountries(data);
      } catch (error) {
        console.error('Error fetching countries:', error);
      }
    };
    
    fetchCountries();
  }, []);
  
  // Handle address search
  const handleAddressSearch = async (query: string) => {
    if (!selectedCountry || query.length < 3) {
      setAddressResults([]);
      return;
    }
    
    // Map the country code to the format expected by the API
    const mappedCountryCode = countryCodeMapping[selectedCountry] || selectedCountry.toLowerCase();
    
    setIsSearchingAddress(true);
    try {
      const results = await officesManagement.searchAddresses(mappedCountryCode, query);
      setAddressResults(results);
    } catch (error) {
      console.error('Error searching addresses:', error);
    } finally {
      setIsSearchingAddress(false);
    }
  };
  
  // Handle country change
  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const countryCode = e.target.value;
    setSelectedCountry(countryCode);
    formik.setFieldValue('address.country', countryCode);
  };
  
  // Handle address selection
  const handleAddressSelect = (address: any) => {
    formik.setFieldValue('address.country', selectedCountry);
    formik.setFieldValue('address.line1', address.addressline1 || address.line1 || '');
    formik.setFieldValue('address.town', address.posttown || address.town || '');
    formik.setFieldValue('address.city', address.posttown || address.town || '');
    formik.setFieldValue('address.postcode', address.postcode || '');
    setAddressResults([]);
    setAddressQuery('');
  };

  const handleCustomFieldChange = (fieldId: string, value: any) => {
    formik.setFieldValue(`custom_fields.${fieldId}`, value);
  };

  // Decide on a default or first-in-list for each dropdown
  // If data is not loaded or empty, fall back to something safe (e.g. '')
  const defaultSex = PEOPLE_SEX[0]?.value || 'SEX_MALE';
  const defaultRole = useMemo(() => {
    // If you have role_data as an array, choose the first or your own default
    // Using 'SOFTWARE_ENGINEER' as an example fallback
    // Adjust for however your role_data is structured
    if (Array.isArray(role_data) && role_data.length > 0) {
      return role_data[0].value;
    }
    return '';
  }, [role_data]);

  const defaultTeam = useMemo(() => {
    if (Array.isArray(team_data) && team_data.length > 0) {
      return team_data[0].id;
    }
    return ''; // fallback
  }, [team_data]);

  const defaultOffice = useMemo(() => {
    if (Array.isArray(office_data) && office_data.length > 0) {
      return office_data[0].id;
    }
    return ''; // fallback
  }, [office_data]);

  const defaultManager = useMemo(() => {
    if (
      people_data?.data &&
      Array.isArray(people_data.data) &&
      people_data.data.length > 0
    ) {
      return people_data.data[0].id;
    }
    return ''; // fallback
  }, [people_data]);

  // Define validation schema
  const validationSchema = Yup.object({
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required'),
    email: Yup.string().email('Invalid email format').required('Email is required'),
    phone: Yup.string().matches(/^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,4}[-\s.]?[0-9]{1,9}$/, 'Invalid phone number format'),
  });

  // Use enableReinitialize to reset initial form values
  // anytime your data arrays finish loading
  const formik = useFormik({
    enableReinitialize: true,
    validationSchema,
    initialValues: {
      firstName: '',
      lastName: '',
      sex: defaultSex,
      email: '',
      role: defaultRole,
      team: defaultTeam,
      office: defaultOffice,
      manager: defaultManager,
      phone: '',
      paidHoliday: 10,
      unpaidHoliday: 10,
      remoteWorkingPerWeek: 2,
      paidSickLeavePerYear: 30,
      address: {
        country: '',
        line1: '',
        town: '',
        city: '',
        postcode: '',
      },
      custom_fields: {} as Record<string, unknown>,
    },
    onSubmit: async ({ firstName, lastName, ...restValues }) => {
      try {
        // Ensure address has the correct structure
        const addressData = {
          country: restValues.address.country,
          line1: restValues.address.line1,
          town: restValues.address.town,
          city: restValues.address.city,
          postcode: restValues.address.postcode
        };

        // Combine first and last name for API
        await mutateAsync({ 
          name: `${firstName} ${lastName}`,
          ...restValues,
          address: addressData 
        });
        toast({
          variant: 'success',
          title: 'User successfully added.',
        });
        refetch();
        closeDialog();
      } catch (error) {
        console.error("Error adding user:", error);
        toast({
          variant: 'error',
          title: 'Failed to add user.',
        });
      }
    },
  });

  // Direct submission function to bypass potential form handling issues
  const handleDirectSubmit = async () => {
    try {
      console.log("Starting direct submission");
      
      // Team and office may hold an id or a { id } option object.
      const idOf = (value: unknown): string =>
        typeof value === 'object' && value !== null
          ? (value as { id?: string }).id || ''
          : (value as string) || '';
      const teamValue = idOf(formik.values.team);
      const officeValue = idOf(formik.values.office);

      // Prepare the data manually with proper structure
      const submitData = {
        name: `${formik.values.firstName} ${formik.values.lastName}`,
        sex: formik.values.sex,
        email: formik.values.email,
        role: formik.values.role || 'EMPLOYEE', // Default to EMPLOYEE if role is empty
        team: teamValue,
        office: officeValue,
        manager: formik.values.manager,
        phone: formik.values.phone,
        paidHoliday: formik.values.paidHoliday,
        unpaidHoliday: formik.values.unpaidHoliday,
        remoteWorkingPerWeek: formik.values.remoteWorkingPerWeek,
        paidSickLeavePerYear: formik.values.paidSickLeavePerYear,
        address: {
          country: formik.values.address.country,
          line1: formik.values.address.line1,
          town: formik.values.address.town,
          city: formik.values.address.city,
          postcode: formik.values.address.postcode
        }
      };
      
      console.log("Submitting data directly:", submitData);
      
      // Make the API call directly
      const API_HOST = process.env.REACT_APP_API_HOST;
      const response = await axios.post(`${API_HOST}/v1/people`, { user: submitData });
      
      console.log("Direct API response:", response.data);
      
      toast({
        variant: 'success',
        title: 'User successfully added.',
      });
      refetch();
      closeDialog();
    } catch (error) {
      console.error("Direct submission error:", error);
      toast({
        variant: 'error',
        title: 'Failed to add user.',
      });
    }
  };

  function closeDialog() {
    formik.resetForm();
    onClose();
    setSecondStep(false);
    setSelectedCountry('');
    setAddressQuery('');
    setAddressResults([]);
  }

  function findError(
    fieldName: keyof typeof formik.initialValues,
  ): string | undefined {
    const error = formik.touched[fieldName]
      ? formik.errors[fieldName]
      : undefined;
    return typeof error === 'string' ? error : undefined;
  }

  return (
    <>
      <Dialog
        isShown={isOpen}
        onCloseComplete={closeDialog}
        hasFooter={false}
        hasHeader={false}
        preventBodyScrolling
      >
        <form className="mb-4" onSubmit={formik.handleSubmit}>
          <div className="flex flex-col">
            <div className="mb-4 flex flex-row justify-between pt-6">
              <div className="rounded-[10px] bg-[#FEE9E9] p-3 text-[#DC2626] shadow-sm">
                <AddUserIcon />
              </div>
              <XMarkIcon className="h-6 w-6 cursor-pointer" onClick={onClose} />
            </div>

            <div className=" flex flex-col gap-1">
              <div className="text-lg font-semibold">Add User</div>
              <div className="text-sm font-normal text-[#475467]">
                Your new project has been created. Invite colleagues to
                collaborate on this project.
              </div>
            </div>

            <div className="my-6 flex flex-row items-center justify-center gap-3">
              {!secondStep ? (
                <div className="rounded-full bg-[#DB7D01] p-3 text-white">
                  <div className="h-5 w-5  text-center text-sm font-medium ">
                    1
                  </div>
                </div>
              ) : (
                <div className="rounded-full bg-[#FDF0DE] p-3">
                  <CheckIcon className="h-5 w-5 " color="#DB7D01" />
                </div>
              )}

              <div className="h-[1px] bg-gray-200 px-8"></div>

              {!secondStep ? (
                <div className="rounded-full bg-[#F9FAFB] p-3 text-[#555A61]">
                  <div className="h-5 w-5  text-center text-sm font-medium ">
                    2
                  </div>
                </div>
              ) : (
                <div className="rounded-full bg-[#DB7D01] p-3 text-white">
                  <div className="h-5 w-5  text-center text-sm font-medium ">
                    2
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-3">
              {!secondStep ? (
                <>
                  <FormGroup label="First Name" htmlFor="firstName-input">
                    <Input
                      id="firstName-input"
                      type="text"
                      name="firstName"
                      error={findError('firstName')}
                      value={formik.values.firstName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="Enter first name"
                    />
                  </FormGroup>
                  
                  <FormGroup label="Last Name" htmlFor="lastName-input">
                    <Input
                      id="lastName-input"
                      type="text"
                      name="lastName"
                      error={findError('lastName')}
                      value={formik.values.lastName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="Enter last name"
                    />
                  </FormGroup>

                  <FormGroup label="Select gender" htmlFor="sex-input">
                    <Select
                      id="sex-input"
                      name="sex"
                      value={formik.values.sex}
                      onChange={formik.handleChange}
                    >
                      {PEOPLE_SEX.map((param, index) => (
                        <option key={index} value={param.value}>
                          {param.title}
                        </option>
                      ))}
                    </Select>
                  </FormGroup>

                  <FormGroup label="Email Address" htmlFor="email-input">
                    <Input
                      id="email-input"
                      type="text"
                      name="email"
                      error={findError('email')}
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      placeholder="you@example.com"
                    />
                  </FormGroup>

                  <FormGroup
                    label="Role"
                    htmlFor="role-input"
                  >
                    <Select
                      id="role-input"
                      name="role"
                      value={formik.values.role}
                      onChange={formik.handleChange}
                    >
                      {/* Example roles. Adjust to match your actual role_data. */}
                      <option value="HR_ADMIN">HR Admin</option>
                      <option value="EMPLOYEE">Employee</option>
                    </Select>
                  </FormGroup>

                  <FormGroup label="Office" htmlFor="office-input">
                    <Select
                      id="office-input"
                      name="office"
                      value={formik.values.office}
                      onChange={formik.handleChange}
                    >
                      {office_data?.map((office: any, index: number) => (
                          <option key={index} value={office.id}>
                            {office.name}
                          </option>
                        ))}
                    </Select>
                  </FormGroup>

                  <FormGroup label="Select team" htmlFor="team-input">
                    <Select
                      id="team-input"
                      name="team"
                      value={formik.values.team}
                      onChange={formik.handleChange}
                    >
                      {team_data?.map((team: any, index: number) => (
                          <option key={index} value={team.id}>
                            {team.name}
                          </option>
                        ))}
                    </Select>
                  </FormGroup>
                </>
              ) : (
                <>
                  <FormGroup label="Direct Manager" htmlFor="manager-input">
                    <Select
                      id="manager-input"
                      name="manager"
                      value={formik.values.manager}
                      onChange={formik.handleChange}
                    >
                      {people_data?.data?.map((person: any, index: number) => (
                        <option key={index} value={person.id}>
                          {person.full_name}
                        </option>
                      ))}
                    </Select>
                  </FormGroup>

                  <FormGroup label="Phone number" htmlFor="phone-input">
                    <Input
                      id="phone-input"
                      type="text"
                      name="phone"
                      error={findError('phone')}
                      value={formik.values.phone}
                      onChange={formik.handleChange}
                      placeholder="+11 222 333 4444"
                    />
                  </FormGroup>

                  {/* Address Fields */}
                  <div className="pt-2 border-t border-gray-200">
                    <div className="text-md font-semibold mb-2">Address Information</div>
                  </div>
                  
                  <FormGroup label="Country" htmlFor="country-input">
                    <Select
                      id="country-input"
                      name="address.country"
                      value={selectedCountry}
                      onChange={handleCountryChange}
                    >
                      <option value="">Select a country</option>
                      {countries.map((country: any, index: number) => {
                        // Handle different country name structures safely
                        let displayName = '';
                        if (typeof country.name === 'string') {
                          displayName = country.name;
                        } else if (country.name && typeof country.name === 'object') {
                          displayName = country.name.common || country.name.official || '';
                        }

                        console.log({ country, displayName })
                        
                        return (
                          <option key={index} value={country.cca2}>
                            {displayName}
                          </option>
                        );
                      })}
                    </Select>
                  </FormGroup>
                  
                  <FormGroup label="Address Search" htmlFor="address-search">
                    <div className="relative">
                      <Input
                        id="address-search"
                        type="text"
                        value={addressQuery}
                        onChange={(e) => {
                          setAddressQuery(e.target.value);
                          handleAddressSearch(e.target.value);
                        }}
                        placeholder="Enter postcode or street name"
                        disabled={!selectedCountry}
                      />
                      
                      {addressResults.length > 0 && (
                        <div className="absolute z-10 mt-1 w-full rounded-md bg-white shadow-lg">
                          <ul className="max-h-60 overflow-auto rounded-md py-1 text-base sm:text-sm">
                            {addressResults.map((address, index) => (
                              <li
                                key={index}
                                className="relative cursor-pointer select-none py-2 px-3 hover:bg-gray-100"
                                onClick={() => handleAddressSelect(address)}
                              >
                                <div className="flex flex-col">
                                  <span className="font-medium text-gray-900">
                                    {address.addressline1 || address.line1 || ''}
                                  </span>
                                  <span className="text-sm text-gray-500">
                                    {address.addressline2 || ''}{address.addressline3 ? `, ${address.addressline3}` : ''}
                                  </span>
                                  <span className="text-xs text-gray-400">
                                    {address.posttown || address.town || ''} {address.postcode || ''}
                                  </span>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {isSearchingAddress && (
                        <div className="absolute right-3 top-2">
                          <span className="loading loading-spinner loading-xs"></span>
                        </div>
                      )}
                    </div>
                  </FormGroup>
                  
                  <FormGroup label="Address Line 1" htmlFor="address-line1">
                    <Input
                      id="address-line1"
                      type="text"
                      name="address.line1"
                      value={formik.values.address.line1}
                      onChange={formik.handleChange}
                      placeholder="Street address"
                    />
                  </FormGroup>
                  
                  <FormGroup label="Town" htmlFor="address-town">
                    <Input
                      id="address-town"
                      type="text"
                      name="address.town"
                      value={formik.values.address.town}
                      onChange={formik.handleChange}
                      placeholder="Town"
                    />
                  </FormGroup>
                  
                  <FormGroup label="City" htmlFor="address-city">
                    <Input
                      id="address-city"
                      type="text"
                      name="address.city"
                      value={formik.values.address.city}
                      onChange={formik.handleChange}
                      placeholder="Enter city"
                    />
                  </FormGroup>
                  
                  <FormGroup label="Zip/Postal Code" htmlFor="address-zipCode">
                    <Input
                      id="address-zipCode"
                      type="text"
                      name="address.postcode"
                      value={formik.values.address.postcode}
                      onChange={formik.handleChange}
                      placeholder="Postal code"
                    />
                  </FormGroup>

                  {customFields.length > 0 && (
                    <div className="pt-2 border-t border-gray-200">
                      <div className="text-md font-semibold mb-2">Custom fields</div>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        {customFields.map((field) => {
                          const value = (formik.values.custom_fields as Record<string, unknown> | undefined)?.[field.system.id];
                          const stringValue = typeof value === 'string' ? value : '';
                          const inputValue =
                            typeof value === 'number' || typeof value === 'string'
                              ? value
                              : '';

                          if (field.details.type === 'boolean') {
                            return (
                              <label key={field.system.id} className="flex items-center gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={Boolean(value)}
                                  onChange={(e) =>
                                    handleCustomFieldChange(field.system.id, e.target.checked)
                                  }
                                />
                                {field.details.name}
                              </label>
                            );
                          }

                          if (field.details.type === 'single_select') {
                            return (
                              <FormGroup key={field.system.id} label={field.details.name}>
                                <Select
                                  value={stringValue}
                                  onChange={(e) =>
                                    handleCustomFieldChange(field.system.id, e.target.value)
                                  }
                                >
                                  <option value="">Select...</option>
                                  {(field.details.options || []).map((opt) => (
                                    <option key={opt} value={opt}>
                                      {opt}
                                    </option>
                                  ))}
                                </Select>
                              </FormGroup>
                            );
                          }

                          if (field.details.type === 'multi_select') {
                            const selected: string[] = Array.isArray(value) ? value : [];
                            return (
                              <FormGroup key={field.system.id} label={field.details.name}>
                                <Select
                                  multiple
                                  value={selected}
                                  onChange={(e) =>
                                    handleCustomFieldChange(
                                      field.system.id,
                                      Array.from(e.target.selectedOptions).map((o) => o.value),
                                    )
                                  }
                                >
                                  {(field.details.options || []).map((opt) => (
                                    <option key={opt} value={opt}>
                                      {opt}
                                    </option>
                                  ))}
                                </Select>
                              </FormGroup>
                            );
                          }

                          if (field.details.type === 'date') {
                            return (
                              <FormGroup key={field.system.id} label={field.details.name}>
                                <Input
                                  type="date"
                                  value={stringValue}
                                  onChange={(e) =>
                                    handleCustomFieldChange(field.system.id, e.target.value)
                                  }
                                />
                              </FormGroup>
                            );
                          }

                          if (field.details.type === 'number') {
                            return (
                              <FormGroup key={field.system.id} label={field.details.name}>
                                <Input
                                  type="number"
                                  value={inputValue}
                                  onChange={(e) =>
                                    handleCustomFieldChange(field.system.id, e.target.value)
                                  }
                                />
                              </FormGroup>
                            );
                          }

                          return (
                            <FormGroup key={field.system.id} label={field.details.name}>
                              <Input
                                type="text"
                                value={stringValue}
                                onChange={(e) =>
                                  handleCustomFieldChange(field.system.id, e.target.value)
                                }
                              />
                            </FormGroup>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <FormGroup label="Paid Holiday" htmlFor="paid-holiday">
                    <Input
                      id="paid-holiday"
                      name="paidHoliday"
                      value={formik.values.paidHoliday}
                      onChange={formik.handleChange}
                      type="number"
                      min={0}
                      max={365}
                      placeholder="Enter days"
                    />
                  </FormGroup>

                  <FormGroup label="Unpaid Holiday" htmlFor="unpaid-holiday">
                    <Input
                      id="unpaid-holiday"
                      name="unpaidHoliday"
                      value={formik.values.unpaidHoliday}
                      onChange={formik.handleChange}
                      type="number"
                      min={0}
                      max={365}
                      placeholder="Enter days"
                    />
                  </FormGroup>

                  <FormGroup
                    label="Remote Working Per Week"
                    htmlFor="remote-working-per-week"
                  >
                    <Input
                      id="remote-working-per-week"
                      name="remoteWorkingPerWeek"
                      value={formik.values.remoteWorkingPerWeek}
                      onChange={formik.handleChange}
                      type="number"
                      min={0}
                      max={365}
                      placeholder="Enter days"
                    />
                  </FormGroup>

                  <FormGroup
                    label="Paid Sick Leave Per Year"
                    htmlFor="paid-sick-leave-per-year"
                  >
                    <Input
                      id="paid-sick-leave-per-year"
                      name="paidSickLeavePerYear"
                      value={formik.values.paidSickLeavePerYear}
                      onChange={formik.handleChange}
                      type="number"
                      min={0}
                      max={365}
                      placeholder="Enter days"
                    />
                  </FormGroup>
                </>
              )}
            </div>

            <div className="mb-6 mt-8 flex flex-row justify-between gap-3">
              <button
                type="button"
                className="w-full rounded-lg border border-[#D0D5DD] bg-white px-[18px] py-[10px] text-base font-semibold text-[#344054] shadow-sm"
                onClick={onClose}
              >
                Cancel
              </button>
              {secondStep ? (
                <button
                  type="button"
                  onClick={handleDirectSubmit}
                  className="w-full rounded-lg bg-[#DC2626] px-[18px] py-[10px] text-base font-semibold text-white shadow-sm"
                >
                  Add
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setSecondStep(true)}
                  className="w-full rounded-lg bg-[#DC2626] px-[18px] py-[10px] text-base font-semibold text-white shadow-sm"
                >
                  Next
                </button>
              )}
            </div>
          </div>
        </form>
      </Dialog>
    </>
  );
}
